import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// We'll define the hooks manually here since the backend tree response 
// for org-units doesn't fit the standard ResourceClient list/meta shape.


export const orgKeys = {
  all: ['org'] as const,
  companies: () => [...orgKeys.all, 'companies'] as const,
  company: (id: string) => [...orgKeys.companies(), id] as const,
  orgUnits: () => [...orgKeys.all, 'org-units'] as const,
  orgUnit: (id: string) => [...orgKeys.orgUnits(), id] as const,
};

export interface Company {
  id: string;
  name: string;
  legalName: string;
  cin: string | null;
  gstin: string | null;
  pan: string;
  registeredAddress: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
  };
  stateCode: string;
  rocNumber: string | null;
  foremanName: string;
  isActive: boolean;
}

export type OrgUnitType = 'HEAD_OFFICE' | 'STATE_OFFICE' | 'ZONE_OFFICE' | 'BRANCH' | 'SUB_BRANCH' | 'SERVICE_POINT';

export interface OrgUnit {
  id: string;
  parentId: string | null;
  unitType: OrgUnitType;
  name: string;
  code: string;
  address: any | null;
  phone: string | null;
  headUserId: string | null;
  isActive: boolean;
}

export interface MyOrgUnit extends OrgUnit {
  membershipRole: string;
  isPrimary: boolean;
}

export interface OrgUnitNode extends OrgUnit {
  children: OrgUnitNode[];
}

export function useCompanies(options?: { throwOnError?: boolean }) {
  return useQuery({
    queryKey: orgKeys.companies(),
    queryFn: async () => {
      const response = await fetcher.get<Company[]>('/staff/v1/companies');
      return response;
    },
    throwOnError: options?.throwOnError ?? false,
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: orgKeys.company(id),
    queryFn: async () => {
      const response = await fetcher.get<Company>(`/staff/v1/companies/${id}`);
      return response;
    },
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<Company, 'id' | 'isActive'>) =>
      fetcher.post<Company>('/staff/v1/companies', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.companies() });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Company> }) =>
      fetcher.put<Company>(`/staff/v1/companies/${id}`, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.companies() });
      queryClient.invalidateQueries({ queryKey: orgKeys.company(id) });
    },
  });
}

export function useOrgUnits() {
  return useQuery({
    queryKey: orgKeys.orgUnits(),
    queryFn: async () => {
      // The backend returns a tree structure directly for this endpoint
      const response = await fetcher.get<OrgUnitNode[]>('/staff/v1/org-units');
      return response;
    },
    select: (data) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      return [];
    },
  });
}

export function useMyOrgUnits() {
  return useQuery({
    queryKey: [...orgKeys.all, 'my-org-units'] as const,
    queryFn: async () => {
      const response = await fetcher.get<MyOrgUnit[]>('/staff/v1/org-units/mine');
      return response;
    },
    select: (data) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      return [];
    },
  });
}

export function useCreateOrgUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<OrgUnit, 'id' | 'isActive'>) =>
      fetcher.post<OrgUnit>('/staff/v1/org-units', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.orgUnits() });
    },
  });
}

export function useUpdateOrgUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<OrgUnit> }) =>
      fetcher.put<OrgUnit>(`/staff/v1/org-units/${id}`, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.orgUnits() });
      queryClient.invalidateQueries({ queryKey: orgKeys.orgUnit(id) });
    },
  });
}

export function useReparentOrgUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      fetcher.put<OrgUnit>(`/staff/v1/org-units/${id}/reparent`, { parentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.orgUnits() });
    },
  });
}

export function useDeactivateOrgUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.delete<void>(`/staff/v1/org-units/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.orgUnits() });
    },
  });
}
